import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  BuildingStorefrontIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckCircleIcon,
  CurrencyEuroIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../lib/supabase';

// ── Types ────────────────────────────────────────────────────────────
interface ProviderInfo {
  provider_id: string;
  provider_name: string;
  price: number;
  quantity: number;
}

interface MergedSize {
  size: string;
  cheapest_price: number;
  total_quantity: number;
  cheapest_provider: string;
  providers: ProviderInfo[];
}

interface ProviderSummary {
  provider_id: string;
  provider_name: string;
  sku: string;
  lowest_price: number;
  total_stock: number;
}

interface MergedProduct {
  title: string;
  vendor: string;
  category: string;
  provider_count: number;
  providers: ProviderSummary[];
  merged_sizes: MergedSize[];
  cheapest_price: number;
  total_stock: number;
}

// ── Provider color map ───────────────────────────────────────────────
const PROVIDER_COLORS: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  'GoldSneakers EU': { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400' },
  'TurumPurum Italia': { bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/30', dot: 'bg-violet-400' },
};

function providerColor(name: string) {
  return PROVIDER_COLORS[name] || { bg: 'bg-white/5', text: 'text-white/60', border: 'border-white/10', dot: 'bg-white/40' };
}

// ── Component ────────────────────────────────────────────────────────
const TestShop: React.FC = () => {
  const [products, setProducts] = useState<MergedProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProduct, setExpandedProduct] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'multi-provider' | 'single-provider'>('all');

  useEffect(() => {
    fetchMergedInventory();
  }, []);

  const fetchMergedInventory = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data, error: rpcErr } = await supabase.rpc('get_merged_inventory', {});
      if (rpcErr) throw rpcErr;
      setProducts((data as MergedProduct[]) || []);
    } catch (e: any) {
      setError(e.message || 'Failed to load merged inventory');
    } finally {
      setLoading(false);
    }
  };

  const filtered = products.filter(p => {
    if (filter === 'multi-provider') return p.provider_count > 1;
    if (filter === 'single-provider') return p.provider_count === 1;
    return true;
  });

  const multiProviderCount = products.filter(p => p.provider_count > 1).length;
  const totalSavings = products
    .filter(p => p.provider_count > 1)
    .reduce((sum, p) => {
      // Compare most expensive vs cheapest provider per product
      const prices = p.providers.map(pr => pr.lowest_price);
      if (prices.length < 2) return sum;
      return sum + (Math.max(...prices) - Math.min(...prices));
    }, 0);

  return (
    <div className="min-h-screen bg-dark px-4 py-8 md:px-8">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center">
              <BuildingStorefrontIcon className="w-5 h-5 text-orange-400" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-white text-2xl">Test Shop</h1>
              <p className="text-white/40 text-sm">Merged inventory view — how your shop sees the catalog</p>
            </div>
          </div>
        </motion.div>

        {/* Stats bar */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.05 }}
        >
          {[
            { label: 'Total Products', value: products.length, accent: 'text-white' },
            { label: 'Multi-Provider', value: multiProviderCount, accent: 'text-orange-400' },
            { label: 'Unique Providers', value: new Set(products.flatMap(p => p.providers.map(pr => pr.provider_id))).size, accent: 'text-white' },
            { label: 'Avg Savings per SKU', value: multiProviderCount > 0 ? `€${(totalSavings / multiProviderCount).toFixed(2)}` : '—', accent: 'text-emerald-400' },
          ].map(stat => (
            <div key={stat.label} className="glass-card rounded-xl p-4">
              <div className="text-white/40 text-xs uppercase tracking-wider mb-1">{stat.label}</div>
              <div className={`font-heading font-bold text-xl ${stat.accent}`}>{stat.value}</div>
            </div>
          ))}
        </motion.div>

        {/* Filter tabs */}
        <div className="flex items-center gap-2">
          {(['all', 'multi-provider', 'single-provider'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                filter === f
                  ? 'bg-orange-500 text-black'
                  : 'bg-white/[0.04] text-white/50 hover:bg-white/[0.08] border border-white/[0.06]'
              }`}
            >
              {f === 'all' ? 'All Products' : f === 'multi-provider' ? `Multi-Provider (${multiProviderCount})` : `Single Provider (${products.length - multiProviderCount})`}
            </button>
          ))}
        </div>

        {/* Loading / Error */}
        {loading && (
          <div className="flex items-center justify-center py-20">
            <ArrowPathIcon className="w-6 h-6 text-orange-400 animate-spin" />
          </div>
        )}
        {error && (
          <div className="glass-card rounded-xl p-6 border border-red-500/20 text-red-400 text-sm">{error}</div>
        )}

        {/* Product list */}
        {!loading && !error && (
          <div className="space-y-3">
            {filtered.map((product, idx) => {
              const isExpanded = expandedProduct === product.title;
              const isMulti = product.provider_count > 1;

              return (
                <motion.div
                  key={product.title}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.25, delay: Math.min(idx * 0.02, 0.3) }}
                  className="glass-card rounded-xl overflow-hidden"
                >
                  {/* Product row */}
                  <button
                    onClick={() => setExpandedProduct(isExpanded ? null : product.title)}
                    className="w-full flex items-center gap-4 p-4 hover:bg-white/[0.02] transition-colors text-left"
                  >
                    {/* Multi-provider badge */}
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold shrink-0 ${
                      isMulti ? 'bg-orange-500/15 text-orange-400 border border-orange-500/30' : 'bg-white/[0.04] text-white/30 border border-white/[0.06]'
                    }`}>
                      {product.provider_count}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-heading font-bold text-white text-sm truncate">{product.title}</span>
                        <span className="text-white/30 text-xs">{product.vendor}</span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {product.providers.map(prov => {
                          const c = providerColor(prov.provider_name);
                          return (
                            <span key={prov.provider_id} className={`inline-flex items-center gap-1.5 text-xs ${c.text}`}>
                              <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`} />
                              {prov.provider_name}
                              <span className="text-white/30">€{prov.lowest_price.toFixed(2)}</span>
                            </span>
                          );
                        })}
                      </div>
                    </div>

                    {/* Merged stats */}
                    <div className="flex items-center gap-6 shrink-0">
                      {isMulti && (
                        <div className="flex items-center gap-1.5">
                          <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                          <span className="text-emerald-400 text-xs font-medium">Cheapest routed</span>
                        </div>
                      )}
                      <div className="text-right">
                        <div className="text-white font-bold text-sm">€{product.cheapest_price.toFixed(2)}</div>
                        <div className="text-white/30 text-xs">{product.total_stock} units</div>
                      </div>
                      {isExpanded ? (
                        <ChevronUpIcon className="w-4 h-4 text-white/30" />
                      ) : (
                        <ChevronDownIcon className="w-4 h-4 text-white/30" />
                      )}
                    </div>
                  </button>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-white/[0.06]"
                    >
                      <div className="p-4 space-y-4">
                        {/* Provider comparison */}
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">Provider Comparison</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {product.providers.map(prov => {
                              const c = providerColor(prov.provider_name);
                              const isCheapest = prov.lowest_price === product.cheapest_price;
                              return (
                                <div
                                  key={prov.provider_id}
                                  className={`rounded-lg p-3 border ${isCheapest ? c.border + ' ' + c.bg : 'border-white/[0.06] bg-white/[0.02]'}`}
                                >
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-2">
                                      <span className={`w-2 h-2 rounded-full ${c.dot}`} />
                                      <span className={`font-medium text-sm ${c.text}`}>{prov.provider_name}</span>
                                    </div>
                                    {isCheapest && isMulti && (
                                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 text-xs border border-emerald-500/20">
                                        <CurrencyEuroIcon className="w-3 h-3" /> Best price
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4 text-xs text-white/50">
                                    <span>SKU: <span className="text-white/70 font-mono">{prov.sku}</span></span>
                                    <span>Price: <span className="text-white/70">€{prov.lowest_price.toFixed(2)}</span></span>
                                    <span>Stock: <span className="text-white/70">{prov.total_stock} pcs</span></span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Size grid — merged inventory */}
                        <div>
                          <h4 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">
                            Merged Size Grid
                            {isMulti && <span className="text-emerald-400 ml-2 normal-case tracking-normal">(inventory combined across providers)</span>}
                          </h4>
                          <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-9 gap-2">
                            {product.merged_sizes.map(sz => {
                              const isMultiSize = sz.providers.length > 1;
                              const cheapC = providerColor(sz.cheapest_provider);
                              return (
                                <div
                                  key={sz.size}
                                  className={`rounded-lg p-2 text-center border ${
                                    sz.total_quantity === 0
                                      ? 'border-red-500/20 bg-red-500/5 opacity-50'
                                      : isMultiSize
                                        ? 'border-orange-500/30 bg-orange-500/5'
                                        : 'border-white/[0.06] bg-white/[0.02]'
                                  }`}
                                >
                                  <div className="font-bold text-white text-sm">EU {sz.size}</div>
                                  <div className="text-white/40 text-xs mt-0.5">{sz.total_quantity} pcs</div>
                                  <div className={`text-xs mt-1 ${cheapC.text}`}>€{sz.cheapest_price.toFixed(2)}</div>
                                  {isMultiSize && (
                                    <div className="flex items-center justify-center gap-0.5 mt-1">
                                      {sz.providers.map(p => (
                                        <span
                                          key={p.provider_id}
                                          className={`w-1.5 h-1.5 rounded-full ${providerColor(p.provider_name).dot}`}
                                          title={`${p.provider_name}: ${p.quantity} pcs @ €${p.price.toFixed(2)}`}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* How routing works — for multi-provider products */}
                        {isMulti && (
                          <div className="rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-3">
                            <div className="flex items-center gap-2 text-emerald-400 text-xs font-medium mb-1">
                              <CheckCircleIcon className="w-4 h-4" />
                              Cheapest Provider Routing
                            </div>
                            <p className="text-white/40 text-xs">
                              When a customer orders this product, FlowStock automatically routes each size to the
                              cheapest available provider. For example, size {product.merged_sizes[0]?.size || '42'} routes
                              to <span className="text-white/70">{product.merged_sizes[0]?.cheapest_provider}</span> at €{product.merged_sizes[0]?.cheapest_price.toFixed(2)}.
                              {product.merged_sizes.length > 1 && (() => {
                                const diff = product.merged_sizes.find(s => s.providers.length > 1 && s.cheapest_provider !== product.merged_sizes[0]?.cheapest_provider);
                                if (diff) return ` Size ${diff.size} routes to ${diff.cheapest_provider} at €${diff.cheapest_price.toFixed(2)}.`;
                                return '';
                              })()}
                            </p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Legend */}
        {!loading && !error && products.length > 0 && (
          <motion.div
            className="glass-card rounded-xl p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <h4 className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-3">Provider Legend</h4>
            <div className="flex flex-wrap gap-4">
              {Object.entries(PROVIDER_COLORS).map(([name, c]) => (
                <div key={name} className="flex items-center gap-2">
                  <span className={`w-3 h-3 rounded-full ${c.dot}`} />
                  <span className={`text-sm ${c.text}`}>{name}</span>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t border-white/[0.06] text-white/30 text-xs space-y-1">
              <p><span className="text-orange-400">Orange borders</span> on sizes = available from multiple providers (inventory merged)</p>
              <p><span className="text-emerald-400">Green badge</span> = FlowStock automatically selects the cheapest provider per size</p>
              <p>Hover colored dots on sizes to see per-provider price and stock breakdown</p>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default TestShop;
